// src/services/sanitarySolutionService.js
// =============================================================================
// Frontend API wrapper for versioned working-solution definitions and concrete
// preparations. Definitions are master data only; preparations are the only
// operations that can post concentrate to stock.
// =============================================================================

import apiClient from "../utils/apiClient";
import { idempotentPost } from "../utils/idempotentStockPosting";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed"
  );
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status || err?.status;
  e.outcomeUnknown = Boolean(err?.outcomeUnknown);
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;
  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[SanitarySolutionService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }
  throw e;
}

function normalizeListPayload(payload, keys = []) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const key of keys) if (Array.isArray(payload[key])) return payload[key];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}


function normalizePagedPayload(payload, keys = [], fallbackPageSize = 25) {
  const items = normalizeListPayload(payload, keys);
  if (Array.isArray(payload)) {
    return { items, pagination: { page: 1, page_size: items.length || fallbackPageSize, total: items.length, page_count: 1 } };
  }
  return {
    items,
    pagination: payload?.pagination || { page: 1, page_size: fallbackPageSize, total: items.length, page_count: 1 },
  };
}

function buildParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

// Definitions / recipes -------------------------------------------------------

export async function listSanitarySolutionDefinitions(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/solutions/definitions", { params: buildParams(params) });
    return normalizePagedPayload(res.data, ["definitions"], Number(params.page_size || params.limit || 25));
  } catch (err) {
    throwApiError("listSanitarySolutionDefinitions", err);
  }
}

export async function fetchSanitarySolutionDefinitions(params = {}) {
  const result = await listSanitarySolutionDefinitions(params);
  return result.items;
}

export async function getSanitarySolutionDefinition(definitionId, params = {}) {
  try {
    const res = await apiClient.get(`/sanitary/solutions/definitions/${definitionId}`, { params: buildParams(params) });
    return res.data;
  } catch (err) {
    throwApiError("getSanitarySolutionDefinition", err);
  }
}

export async function createSanitarySolutionDefinition(payload = {}) {
  try {
    const res = await apiClient.post("/sanitary/solutions/definitions", payload);
    return res.data;
  } catch (err) {
    throwApiError("createSanitarySolutionDefinition", err);
  }
}

export async function updateSanitarySolutionDefinition(definitionId, payload = {}) {
  try {
    const res = await apiClient.put(`/sanitary/solutions/definitions/${definitionId}`, payload);
    return res.data;
  } catch (err) {
    throwApiError("updateSanitarySolutionDefinition", err);
  }
}

export async function saveSanitarySolutionDefinitionEditor(definitionId, payload = {}) {
  try {
    const res = await apiClient.put(`/sanitary/solutions/definitions/${definitionId}/editor`, payload);
    return res.data;
  } catch (err) {
    throwApiError("saveSanitarySolutionDefinitionEditor", err);
  }
}

export async function deleteSanitarySolutionDefinition(definitionId) {
  try {
    const res = await apiClient.delete(`/sanitary/solutions/definitions/${definitionId}`);
    return res.data;
  } catch (err) {
    throwApiError("deleteSanitarySolutionDefinition", err);
  }
}

export async function setSanitarySolutionDefinitionStatus(definitionId, status) {
  try {
    const res = await apiClient.patch(`/sanitary/solutions/definitions/${definitionId}/status`, { status });
    return res.data;
  } catch (err) {
    throwApiError("setSanitarySolutionDefinitionStatus", err);
  }
}

export async function createSanitarySolutionDefinitionVersion(definitionId, payload = {}) {
  try {
    const res = await apiClient.post(`/sanitary/solutions/definitions/${definitionId}/versions`, payload);
    return res.data;
  } catch (err) {
    throwApiError("createSanitarySolutionDefinitionVersion", err);
  }
}

// Concrete preparations -------------------------------------------------------

export async function listPendingSanitarySolutionPreparations(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/solutions/pending", { params: buildParams(params) });
    return normalizePagedPayload(res.data, ["pending"], Number(params.page_size || params.limit || 25));
  } catch (err) {
    throwApiError("listPendingSanitarySolutionPreparations", err);
  }
}

export async function fetchPendingSanitarySolutionPreparations(params = {}) {
  const result = await listPendingSanitarySolutionPreparations(params);
  return result.items;
}

export async function listSanitarySolutionPreparations(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/solutions", { params: buildParams(params) });
    return normalizePagedPayload(res.data, ["preparations", "solutions"], Number(params.page_size || params.limit || 25));
  } catch (err) {
    throwApiError("listSanitarySolutionPreparations", err);
  }
}

export async function fetchSanitarySolutionPreparations(params = {}) {
  const result = await listSanitarySolutionPreparations(params);
  return result.items;
}

export async function getSanitarySolutionPreparation(preparationId) {
  try {
    const res = await apiClient.get(`/sanitary/solutions/${preparationId}`);
    return res.data;
  } catch (err) {
    throwApiError("getSanitarySolutionPreparation", err);
  }
}

export async function previewSanitarySolutionPreparation(payload = {}) {
  try {
    const res = await apiClient.post("/sanitary/solutions/preview", payload);
    return res.data;
  } catch (err) {
    throwApiError("previewSanitarySolutionPreparation", err);
  }
}

export async function createSanitarySolutionPreparation(payload = {}) {
  try {
    const res = payload.consumption_entry_id || payload.consumptionEntryId
      ? await apiClient.post("/sanitary/solutions", payload)
      : await idempotentPost(apiClient, "/sanitary/solutions", payload);
    return res.data;
  } catch (err) {
    throwApiError("createSanitarySolutionPreparation", err);
  }
}

export async function setSanitarySolutionPreparationStatus(preparationId, payload = {}) {
  try {
    const res = await apiClient.patch(`/sanitary/solutions/${preparationId}/status`, payload);
    return res.data;
  } catch (err) {
    throwApiError("setSanitarySolutionPreparationStatus", err);
  }
}

export default {
  listSanitarySolutionDefinitions,
  fetchSanitarySolutionDefinitions,
  getSanitarySolutionDefinition,
  createSanitarySolutionDefinition,
  updateSanitarySolutionDefinition,
  saveSanitarySolutionDefinitionEditor,
  deleteSanitarySolutionDefinition,
  setSanitarySolutionDefinitionStatus,
  createSanitarySolutionDefinitionVersion,
  listPendingSanitarySolutionPreparations,
  fetchPendingSanitarySolutionPreparations,
  listSanitarySolutionPreparations,
  fetchSanitarySolutionPreparations,
  getSanitarySolutionPreparation,
  previewSanitarySolutionPreparation,
  createSanitarySolutionPreparation,
  setSanitarySolutionPreparationStatus,
};
